import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useCart } from 'contexts/CartContext';

const PublicHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeHover, setActiveHover] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount, setCartOpen } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user?.role) return '/attendee/orders';
    switch (user.role.toLowerCase()) {
      case 'admin': return '/admin';
      case 'instructor': return '/attendee/orders';
      default: return '/attendee/orders';
    }
  };

  const getDashboardLabel = () => {
    if (!user?.role) return 'Dashboard';
    switch (user.role.toLowerCase()) {
      case 'admin': return 'Admin';
      case 'instructor': return 'Instructor';
      default: return 'Dashboard';
    }
  };

  const webinarMenuItems = [
    {
      type: 'live',
      title: 'Live Webinars',
      description: 'Real-time compliance training',
      icon: 'Video',
      path: '/webinars/live',
      badge: 'LIVE',
      gradient: 'from-red-500 to-orange-500',
      bgGradient: 'from-red-50 to-orange-50'
    },
    {
      type: 'recorded',
      title: 'Recorded Webinars',
      description: 'On-demand learning library',
      icon: 'PlayCircle',
      path: '/webinars/recorded',
      badge: 'ON-DEMAND',
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50'
    }
  ];

  const navigationItems = [
    { name: 'About', path: '/about', icon: 'Info' },
    { name: 'Live Webinars', path: '/webinars/live', icon: 'Video' },
    { name: 'Recorded', path: '/webinars/recorded', icon: 'PlayCircle' },
    { name: 'Contact', path: '/contact', icon: 'MessageSquare' }
  ];

  const renderDesktopNavItem = (item, index) => {
    const isActive = location.pathname === item.path;
    
    return (
      <motion.button
        key={item.name}
        onClick={() => handleNavigation(item.path)}
        onMouseEnter={() => setActiveHover(item.path)}
        onMouseLeave={() => setActiveHover(null)}
        className="relative px-4 py-3"
      >
        <div className="flex items-center space-x-2">
          <motion.div
            animate={{
              rotate: isActive || activeHover === item.path ? 360 : 0
            }}
            transition={{ duration: 0.5 }}
            className="w-5 h-5 flex items-center justify-center"
          >
            <Icon 
              name={item.icon} 
              size={16}
              className={isActive ? 'text-emerald-500' : 'text-gray-600'}
            />
          </motion.div>
          
          <span className={`font-medium text-sm transition-colors duration-300 ${
            isActive ? 'text-emerald-600' : 'text-gray-700 hover:text-emerald-600'
          }`}>
            {item.name}
          </span>
          
          {item.badge && (
            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
              item.type === 'live' 
                ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
                : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
            }`}>
              {item.badge}
            </span>
          )}
        </div>

        {/* Animated underline */}
        <motion.div
          className="absolute bottom-2 left-4 right-4 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500"
          initial={false}
          animate={{
            scaleX: isActive || activeHover === item.path ? 1 : 0,
            opacity: isActive || activeHover === item.path ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
          style={{ originX: 0 }}
        />
        
        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl"
          initial={false}
          animate={{
            opacity: isActive || activeHover === item.path ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.button>
    );
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        type: "spring",
        stiffness: 100,
        damping: 20,
        delay: 0.1
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl border-b border-emerald-100/50 shadow-2xl shadow-emerald-500/5'
          : 'bg-white/90 backdrop-blur-lg border-b border-gray-100/30'
      }`}
    >
      {/* Header background effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 100, 0],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-green-500"
        />
      </div>

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            
            {/* Logo Section */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center space-x-3 cursor-pointer group"
              onClick={() => handleNavigation('/')}
            >
              <div className="relative">
                {/* Animated logo background */}
                <motion.div
                  animate={{
                    rotate: [0, 360]
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute inset-0 w-10 h-10 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-2xl opacity-20"
                />
                
                {/* Logo container */}
                <motion.div
                  whileHover={{ rotate: 8 }}
  className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-white shadow-lg group-hover:shadow-xl transition-shadow duration-300"
>
  <img
    src="/assets/logo2.jpeg" 
    alt="WorkForceSkilled Logo"
    className="w-7 h-7 object-contain"
  />

                  {/* Logo shine effect */}
                  <motion.div
                    animate={{
                      x: ['-100%', '200%']
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: 1
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  />
                </motion.div>
              </div>
              
              {/* Logo text */}
              <div className="flex flex-col">
                <motion.div 
                  className="text-lg font-bold bg-gradient-to-r from-emerald-700 via-green-600 to-teal-700 bg-clip-text text-transparent leading-tight"
                  whileHover={{ scale: 1.01 }}
                >
                  WorkForceSkilled
                </motion.div>
                <div className="text-xs text-gray-500 font-medium">
                  Professional Learning Hub
                </div>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item, index) => renderDesktopNavItem(item, index))}
            </nav>

            {/* Right Section - Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Cart Button */}
              <motion.button
                onClick={() => handleNavigation('/cart')}
                className="relative p-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="relative">
                  <motion.div
                    animate={{
                      rotate: activeHover === 'cart' ? [0, -10, 10, 0] : 0
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon
                      name="ShoppingCart"
                      size={20}
                      className="text-gray-700 group-hover:text-emerald-600 transition-colors duration-300"
                    />
                  </motion.div>
                  
                  {itemCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <span className="text-xs font-bold text-white">
                        {itemCount > 9 ? '9+' : itemCount}
                      </span>
                    </motion.div>
                  )}
                </div>
                
                {/* Cart glow effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full blur-xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: activeHover === 'cart' ? 0.3 : 0,
                    scale: activeHover === 'cart' ? 1.2 : 0.8
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>

              {/* User/Auth Section */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <motion.button
                    onClick={() => handleNavigation(getDashboardPath())}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl hover:from-emerald-500/20 hover:to-teal-500/20 transition-all duration-300 group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center shadow-md"
                    >
                      <span className="text-white text-sm font-bold">
                        {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                      </span>
                    </motion.div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-800 group-hover:text-emerald-700">
                        {getDashboardLabel()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user?.email?.split('@')[0] || 'User'}
                      </div>
                    </div>
                  </motion.button>
                  
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="text-gray-600 hover:text-red-600 hover:bg-red-50 px-3"
                      iconName="LogOut"
                      iconPosition="left"
                    >
                      Logout
                    </Button>
                  </motion.div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="ghost"
                      onClick={() => handleNavigation('/login')}
                      className="text-gray-700 hover:text-emerald-600 px-6 font-medium group relative overflow-hidden"
                    >
                      <span className="relative z-10">Login</span>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: 0 }}
                        transition={{ duration: 0.3 }}
                      />
                    </Button>
                  </motion.div>
                  
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={() => handleNavigation('/register')}
                      className="px-6 py-2.5 font-medium bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300"
                      iconName="UserPlus"
                      iconPosition="right"
                    >
                      Sign Up
                    </Button>
                  </motion.div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl hover:bg-emerald-50 transition-colors duration-200"
              whileTap={{ scale: 0.9 }}
            >
              <div className="relative w-6 h-6">
                <motion.div
                  animate={isMobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                  className="absolute w-6 h-0.5 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full"
                />
                <motion.div
                  animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                  className="absolute top-2 w-6 h-0.5 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full"
                />
                <motion.div
                  animate={isMobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                  className="absolute top-4 w-6 h-0.5 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full"
                />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-emerald-100/50 shadow-2xl overflow-hidden"
            >
              <div className="px-4 py-6 space-y-6">
                
                {/* User Info */}
                {isAuthenticated && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200/50"
                  >
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-md"
                    >
                      <span className="text-white font-bold text-lg">
                        {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                      </span>
                    </motion.div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900">
                        {user?.first_name && user?.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user?.username || 'User'
                        }
                      </div>
                      <div className="text-sm text-gray-600">{user?.email}</div>
                      <div className="text-xs text-emerald-600 font-semibold capitalize">
                        {user?.role} • {getDashboardLabel()}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Cart Summary */}
                {itemCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/50"
                  >
                    <button
                      onClick={() => handleNavigation('/cart')}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center space-x-3">
                        <motion.div
                          whileHover={{ rotate: 15 }}
                          className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-md"
                        >
                          <Icon name="ShoppingCart" size={18} className="text-white" />
                        </motion.div>
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">Shopping Cart</div>
                          <div className="text-sm text-gray-600">{itemCount} items</div>
                        </div>
                      </div>
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="text-emerald-600"
                      >
                        <Icon name="ArrowRight" size={16} />
                      </motion.div>
                    </button>
                  </motion.div>
                )}

                {/* Navigation Links */}
                <div className="grid grid-cols-2 gap-3">
                  {navigationItems.map((item, index) => (
                    <motion.button
                      key={item.name}
                      onClick={() => handleNavigation(item.path)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`p-4 rounded-xl border transition-all duration-300 ${
                        location.pathname === item.path
                          ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                      }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          location.pathname === item.path
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                            : 'bg-gray-200'
                        }`}>
                          <Icon
                            name={item.icon}
                            size={20}
                            className={location.pathname === item.path ? 'text-white' : 'text-gray-600'}
                          />
                        </div>
                        <span className={`font-medium text-sm ${
                          location.pathname === item.path ? 'text-emerald-700' : 'text-gray-700'
                        }`}>
                          {item.name}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Webinar Types */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Training Types
                  </h3>
                  <div className="space-y-2">
                    {webinarMenuItems.map((item, index) => (
                      <motion.button
                        key={item.type}
                        onClick={() => handleNavigation(item.path)}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                        whileHover={{ x: 5 }}
                        className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-emerald-200 transition-all duration-300 group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-r ${item.gradient} shadow-md`}>
                            <Icon name={item.icon} size={18} className="text-white" />
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-gray-900 group-hover:text-emerald-700">
                              {item.title}
                            </div>
                            <div className="text-xs text-gray-600">
                              {item.description}
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${item.gradient} text-white`}>
                          {item.badge}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Auth Buttons */}
                <div className="space-y-3 pt-4 border-t border-emerald-200/50">
                  {isAuthenticated ? (
                    <>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Button
                          fullWidth
                          onClick={() => handleNavigation(getDashboardPath())}
                          className="justify-center bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg"
                          iconName="LayoutDashboard"
                        >
                          Go to Dashboard
                        </Button>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                      >
                        <Button
                          variant="outline"
                          fullWidth
                          onClick={handleLogout}
                          className="justify-center text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          iconName="LogOut"
                        >
                          Logout
                        </Button>
                      </motion.div>
                    </>
                  ) : (
                    <>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Button
                          variant="outline"
                          fullWidth
                          onClick={() => handleNavigation('/login')}
                          className="justify-center border-emerald-200 text-emerald-600 hover:text-emerald-700 hover:border-emerald-300"
                          iconName="LogIn"
                        >
                          Login
                        </Button>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                      >
                        <Button
                          fullWidth
                          onClick={() => handleNavigation('/register')}
                          className="justify-center bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg"
                          iconName="UserPlus"
                        >
                          Create Account
                        </Button>
                      </motion.div>
                    </>
                  )}
                </div>

                {/* Browse All */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="pt-4 border-t border-emerald-200/50"
                >
                  <button
                    onClick={() => handleNavigation('/webinars/live')}
                    className="w-full flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl hover:from-emerald-100 hover:to-teal-100 transition-all duration-300 group"
                  >
                    <Icon name="Search" size={18} className="text-emerald-600" />
                    <span className="font-medium text-emerald-700">Browse All Training</span>
                    <Icon name="ArrowRight" size={16} className="text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default PublicHeader;